import { Stack, Typography, useTheme } from '@mui/material';
import { Check } from '@mui/icons-material';
import { RoCarePlanPages } from 'shared-components/enums';
import { CAREPLAN_PAGES } from '../Constants';
import { useTenantAppConfig } from 'op-utils/useTenantAppConfig';
import ROCareplanContext from '../CareplanContext';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { UserContext } from 'op-contexts/index';
import { GET_CAREPLAN_PRESCRIPTIONS } from '../Queries';
import SimulationSubSteps from './SimulationSubSteps';
import PrescriptionSubSteps from './PrescriptionSubSteps';
import VolumingAndSpecialPhysicsConsultSubSteps from './VolumingSubSteps';
import React from 'react';

export interface Step {
  idx: number;
  text: string;
  page: typeof CAREPLAN_PAGES[keyof typeof CAREPLAN_PAGES];
}

interface Props {
  patientId: string;
  careplanId: string;
  diagnosisId?: string;
  steps: Step[];
  currentStepIndex: number;
  isExistingDiagnosis: boolean;
  previousUrl: string;
  disabled: boolean;
  statusText: string;
}
const isCpot = (status: string) => status === 'cpot';

const routeToEnum = (
  route: typeof CAREPLAN_PAGES[keyof typeof CAREPLAN_PAGES],
  showSpecialPhysicsConsultPage: boolean,
): RoCarePlanPages => {
  switch (route) {
    case CAREPLAN_PAGES.CREATE:
      return RoCarePlanPages.DX_LANDING_PG;
    case CAREPLAN_PAGES.DIAGNOSIS_A:
      return RoCarePlanPages.DX_NEW_DIAGNOSIS_PG;
    case CAREPLAN_PAGES.DIAGNOSIS_B:
      return RoCarePlanPages.DX_SELECT_DIAGNOSIS_PG;
    case CAREPLAN_PAGES.STAGING:
      return RoCarePlanPages.DX_STAGING_PG;
    case CAREPLAN_PAGES.INTAKE:
      return RoCarePlanPages.DX_INTAKE_PG;
    case CAREPLAN_PAGES.TREATMENT_INFO:
      return RoCarePlanPages.CP_TREATMENT_INFO_PG;
    case CAREPLAN_PAGES.TEMPLATE_SELECTION:
      return RoCarePlanPages.CP_CAREPLAN_SELECTION_PG;
    case CAREPLAN_PAGES.SITESETUP:
      return RoCarePlanPages.CP_SITESETUP_PG;
    case CAREPLAN_PAGES.SIMULATION:
      return RoCarePlanPages.TM_SIMULATION_PG;
    case CAREPLAN_PAGES.PRESCRIPTION:
      return RoCarePlanPages.TM_PRESCRIPTION_PG;
    case CAREPLAN_PAGES.MRL_SAFETY_SCREENING:
      return RoCarePlanPages.TM_MRL_SAFETY_SCREENING_PG;
    case CAREPLAN_PAGES.VOLUMING:
      return RoCarePlanPages.TM_VOLUMING_PG;
    case CAREPLAN_PAGES.SPECIAL_PHYSICS_CONSULT:
      return RoCarePlanPages.TM_SPECIAL_PHYSICS_CONSULT;
    case CAREPLAN_PAGES.SUBMISSION:
      return showSpecialPhysicsConsultPage ? RoCarePlanPages.TM_SUBMISSION_PG_US : RoCarePlanPages.TM_SUBMISSION_PG;
    default:
      return RoCarePlanPages.DX_LANDING_PG;
  }
};

const ROStepperSection = ({
  steps,
  currentStepIndex,
  previousUrl,
  disabled,
  isExistingDiagnosis,
  patientId,
  careplanId,
  diagnosisId,
  statusText,
}: Props): JSX.Element => {
  const previousUrlIsIntake = previousUrl.endsWith(CAREPLAN_PAGES.INTAKE);
  const previousUrlIsStaging = previousUrl.endsWith(CAREPLAN_PAGES.STAGING);
  const context = useContext(ROCareplanContext);
  const theme = useTheme();
  const history = useHistory();
  const userContext = useContext(UserContext);
  const { hasFeature } = userContext;
  const isSimpleDiagnosisSelectionEnabled = hasFeature('SimpleDiagnosisSelection');
  const tenantAppConfig = useTenantAppConfig();
  const showSpecialPhysicsConsultPage = tenantAppConfig.SpecialPhysicsConsultVisible;

  const { data: prescriptionsData } = useQuery(GET_CAREPLAN_PRESCRIPTIONS, {
    variables: { careplanId },
    skip: !careplanId || careplanId === 'create' || isNaN(Number(careplanId)),
  });

  const generatePageLink = (urlSuffix: string) => {
    const url = `/radiation/patient/${patientId}/careplan/${careplanId}/`;

    if (urlSuffix === CAREPLAN_PAGES.CREATE) {
      return `${url}diagnosis/${diagnosisId}/${CAREPLAN_PAGES.DIAGNOSIS_A}/${CAREPLAN_PAGES.DIAGNOSIS_B}`;
    }
    if ([CAREPLAN_PAGES.STAGING, CAREPLAN_PAGES.INTAKE].includes(urlSuffix)) {
      return `${url}diagnosis/${diagnosisId}/${urlSuffix}`;
    }
    return url + urlSuffix;
  };

  const stepIndex = currentStepIndex;

  return (
    <>
      {steps.map((step: Step, index: number): JSX.Element => {
        const isCurrent = stepIndex === step.idx;
        const isComplete = stepIndex ? stepIndex > step.idx : false;
        const simulationIdx = steps.find((s) => s.page === CAREPLAN_PAGES.SIMULATION)?.idx ?? Number.POSITIVE_INFINITY;
        const isBeforeSimulation = step.idx < simulationIdx;
        const isSimulationStep = step.page === CAREPLAN_PAGES.SIMULATION;
        const isPrescriptionStep = step.page === CAREPLAN_PAGES.PRESCRIPTION;
        const isVolumingStep = step.page === CAREPLAN_PAGES.VOLUMING;
        const isSpecialPhysicsConsultStep =
          step.page === CAREPLAN_PAGES.SPECIAL_PHYSICS_CONSULT && showSpecialPhysicsConsultPage;

        const isDisabled = () => {
          // Determine if we should disable the intake or staging page, using the same logic as back navigation on the
          // treatment info page
          const disableForIntake = step.page === RoCarePlanPages.DX_INTAKE_PG && !previousUrlIsIntake;
          const disableForStaging =
            step.page === RoCarePlanPages.DX_STAGING_PG && !(previousUrlIsIntake || previousUrlIsStaging);
          const disableForDiagnosis =
            !isSimpleDiagnosisSelectionEnabled &&
            isExistingDiagnosis &&
            step.idx <= Number(RoCarePlanPages.DX_INTAKE_PG);
          const disableForCpot = isCpot(statusText) && isBeforeSimulation;
          return (
            stepIndex < step.idx ||
            disableForCpot ||
            disableForDiagnosis ||
            disableForIntake ||
            disableForStaging ||
            disabled ||
            !diagnosisId
          );
        };
        const disableStep = isDisabled();

        return (
          <React.Fragment key={step.idx}>
            <div
              style={{
                background: isCurrent ? 'white' : 'transparent',
                borderLeft: `5px solid ${isCurrent ? theme.palette.primary.main : 'transparent'}`,
                borderRadius: '4px 0 0 4px',
                cursor: disableStep ? 'not-allowed' : 'pointer',
              }}
              data-testid={`nav-step-${step.idx}${disableStep ? '-disabled' : ''}`}
              key={step.idx}
              onClick={(): void => {
                if (disableStep || isCurrent) return;
                const pageLink = generatePageLink(step.page);
                context.setPage(routeToEnum(step.page, showSpecialPhysicsConsultPage));
                history.push(pageLink);
              }}>
              <Stack
                direction="row"
                alignItems="center"
                gap="8px"
                sx={{
                  borderRadius: '0 4px 4px 0',
                  padding: '8px',
                  background: isCurrent ? theme.palette.secondary.light : 'inherit',
                }}>
                {isComplete ? (
                  <Check
                    color="primary"
                    data-testid={`nav-step-${step.idx}-complete`}
                    sx={{
                      backgroundColor: theme.palette.primary.contrastText,
                      backgroundSize: '24px',
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: '16px',
                      color: theme.palette.primary.main,
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    style={{
                      background: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      width: '24px',
                      height: '24px',
                      border: `2px solid ${theme.palette.primary.main}`,
                      lineHeight: '20px',
                      borderRadius: '100%',
                      textAlign: 'center',
                    }}>
                    {stepIndex <= step.idx ? step.idx : null}
                  </Typography>
                )}
                <Typography
                  textTransform="capitalize"
                  variant="body1"
                  data-testid={`nav-step-text-${step.idx}${isCurrent ? '-current' : ''}`}>
                  {step.text}
                </Typography>
              </Stack>
            </div>

            {isSimulationStep && (
              <SimulationSubSteps careplanId={careplanId} isVisible={isCurrent} data={prescriptionsData} />
            )}
            {isPrescriptionStep && (
              <PrescriptionSubSteps careplanId={careplanId} isVisible={isCurrent} data={prescriptionsData} />
            )}
            {isVolumingStep && (
              <VolumingAndSpecialPhysicsConsultSubSteps
                careplanId={careplanId}
                isVisible={isCurrent}
                data={prescriptionsData}
              />
            )}
            {isSpecialPhysicsConsultStep && (
              <VolumingAndSpecialPhysicsConsultSubSteps
                careplanId={careplanId}
                isVisible={isCurrent}
                data={prescriptionsData}
              />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default ROStepperSection;
